import { useState } from 'react';

import { ConsultingCompanyMaxProjectsCountProgressBarDocument } from '@/graphql/generated';
import { ConsultingCompany } from '@/slices/consultingCompany/consultingCompany.types';
import licensingApi from '@/slices/licensing/licensing.api';

import { Loader } from '@/components/generic/Loader';
import { useToast } from '@/components/layout/Toast';
import { useApolloRefetcher } from '@/hooks/useApolloRefetcher';
import { useTranslation } from '@/hooks/useTranslation';

export function LicenseEditor({ company }: { company: ConsultingCompany }) {
  const { t } = useTranslation();
  const { callRefetchQueries } = useApolloRefetcher();
  const [maxProjectsCount, setMaxProjectsCount] = useState<number>(
    company.license?.licensesCount || 0,
  );
  const toast = useToast();

  const [updateCompanyLicense] =
    licensingApi.endpoints.updateCompanyLicense.useMutation();

  const [isLoading, setIsLoading] = useState(false);
  const saveLicenseCount = (newMaxCount: number) => {
    setIsLoading(true);
    setMaxProjectsCount(newMaxCount);
    updateCompanyLicense({
      companyId: company.id,
      licensesCount: newMaxCount,
    })
      .unwrap()
      .then(() => {
        toast.openToastWithMessage(t('admin:companies.toast.limit_updated'));
        callRefetchQueries([
          {
            query: ConsultingCompanyMaxProjectsCountProgressBarDocument,
            variables: { companyId: company.id },
          },
        ]);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => setIsLoading(false));
  };

  const isUnlimited = maxProjectsCount === -1;
  return (
    <div className="flex items-center gap-2">
      <label className="form-input-label">
        {t('admin:companies.list.item.max_projects')}
      </label>
      {isUnlimited && (
        <div className="tag red">
          {t('admin:companies.list.item.unlimited')}
        </div>
      )}
      {isLoading && <Loader />}
      <input
        type="number"
        className="form-input-text w-20"
        disabled={isLoading}
        min={-1}
        value={maxProjectsCount}
        onChange={(e) => saveLicenseCount(parseInt(e.target.value) || 0)}
      />
    </div>
  );
}
